/**
 * Wizard Steg 2: Översikt & Validering
 *
 * Visar resultaträkning och balansräkning med expanderbara/redigerbara sektioner.
 * Användaren kan korrigera värden som importerats från SIE-filen.
 */

import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  XCircle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Loader2,
  Info,
  ClipboardList,
  BarChart2,
  Plus,
  RefreshCw,
  Trash2,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import {
  reportsApi,
  type AnnualReport,
  type ParsedSieData,
  type UserInput,
  type SieOverrides,
  type EquityChangesInput,
} from "@/lib/api";
import { formatNumber, formatPercent } from "@/lib/format";
import { toast } from "sonner";
import {
  EditableNumberField,
  ReadOnlyNumberField,
} from "@/components/EditableNumberField";
import { KontrollbalansrakningInfoModal } from "@/components/wizard/KontrollbalansrakningInfoModal";
import { AccountingStandardSelector } from "@/components/wizard/AccountingStandardSelector";
import { isManualEntry } from "@/lib/manual-entry";

// All expandable section names
const ALL_SECTIONS = [
  "revenue",
  "expenses",
  "financial",
  "other",
  "fixedAssets",
  "currentAssets",
  "equity",
  "liabilities",
];

// Sections per block for expand/collapse all
const INCOME_STATEMENT_SECTIONS = ["revenue", "expenses", "financial", "other"];
const BALANCE_SHEET_SECTIONS = ["fixedAssets", "currentAssets", "equity", "liabilities"];

interface Step2OverviewProps {
  report: AnnualReport;
  onUpdate: (report: AnnualReport) => void;
  /** Override save (for guest/public wizard) */
  onSaveInput?: (data: Record<string, unknown>) => Promise<{ success: boolean; report?: AnnualReport; error?: string }>;
  /** Override navigation (for guest/public wizard or K3 decimal steps) */
  onNavigateStep?: (step: number | string) => void;
  /** Previous step (default: 1, guest mode: 2) */
  prevStep?: number | string;
  /** Next step (default: 3, guest mode: 4, K3: "2.5") */
  nextStep?: number | string;
}

// Helper to get nested value with path
function getNestedValue(obj: any, path: string): number | undefined {
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
}

// Helper to set nested value with path
function setNestedValue(obj: any, path: string, value: number): any {
  const parts = path.split(".");
  const result = { ...obj };
  let current = result;

  for (let i = 0; i < parts.length - 1; i++) {
    current[parts[i]] = { ...current[parts[i]] };
    current = current[parts[i]];
  }

  current[parts[parts.length - 1]] = value;
  return result;
}

// Helper to remove nested value
function removeNestedValue(obj: any, path: string): any {
  const parts = path.split(".");
  const result = { ...obj };
  let current = result;

  for (let i = 0; i < parts.length - 1; i++) {
    if (!current[parts[i]]) return result;
    current[parts[i]] = { ...current[parts[i]] };
    current = current[parts[i]];
  }

  delete current[parts[parts.length - 1]];
  return result;
}

export function Step2Overview({ report, onUpdate, onSaveInput, onNavigateStep, prevStep = 1, nextStep = 3 }: Step2OverviewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const sieData = report.parsedSieData as ParsedSieData | null;
  const userInput = report.userInput as UserInput;

  // Check if we arrived here after switching standard via "(byt)" dialog
  // This helps handle the race condition between state update and navigation
  const standardJustConfirmed = (location.state as { standardJustConfirmed?: boolean } | null)?.standardJustConfirmed === true;

  // Type for flerårsöversikt entries
  type PreviousYearEntry = { year: string; netSales: number; resultAfterFinancialItems: number; solidityPercent: number };

  const [overrides, setOverrides] = useState<SieOverrides>(
    userInput.overrides || {}
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedOverrides, setLastSavedOverrides] = useState<SieOverrides>(
    userInput.overrides || {}
  );
  const [lastSavedPreviousYears, setLastSavedPreviousYears] = useState<PreviousYearEntry[]>(
    userInput.previousYears || []
  );

  // Refs for latest values to avoid stale closures in async callbacks
  const overridesRef = useRef(overrides);
  const previousYearsRef = useRef<PreviousYearEntry[]>([]);
  const userInputRef = useRef(userInput);
  overridesRef.current = overrides;
  userInputRef.current = userInput;

  // Expanded sections state - default all expanded for manual entry
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    // If manual entry, default all sections expanded
    if (sieData && isManualEntry(sieData)) {
      return new Set(ALL_SECTIONS);
    }
    return new Set();
  });

  // Ensure sections are expanded for manual entry (handles case where sieData loads after mount)
  useEffect(() => {
    if (sieData && isManualEntry(sieData)) {
      setExpandedSections(new Set(ALL_SECTIONS));
    }
  }, [sieData]);

  // Kontrollbalansräkning modal state
  const [showKontrollbalansModal, setShowKontrollbalansModal] = useState(false);

  // Capital deficiency acknowledgement
  const [capitalDeficiencyAcknowledged, setCapitalDeficiencyAcknowledged] = useState(false);

  // Extract accountingStandardConfirmed for proper React dependency tracking
  const isStandardConfirmed = !!(userInput as Record<string, unknown>).accountingStandardConfirmed;

  // Accounting standard selector modal state
  // IMPORTANT: Initialize to FALSE to avoid modal flash on remount
  // The modal should only show via explicit user action or first-time detection in useEffect
  const [showAccountingStandardSelector, setShowAccountingStandardSelector] = useState(false);

  // Track if we've done the initial accounting standard check (to avoid re-showing on re-renders)
  const hasCheckedAccountingStandard = useRef(false);

  // Show modal on FIRST mount if standard hasn't been confirmed yet
  // This runs once per mount, after the component is ready
  useEffect(() => {
    // Skip if we've already checked (prevents re-showing on re-renders)
    if (hasCheckedAccountingStandard.current) return;
    hasCheckedAccountingStandard.current = true;

    const confirmed = !!(report.userInput as Record<string, unknown>)?.accountingStandardConfirmed;
    // Only show modal if NOT confirmed AND NOT coming from "(byt)" dialog
    if (!confirmed && !standardJustConfirmed) {
      setShowAccountingStandardSelector(true);
    }
  }, []); // Empty deps - only run on mount

  // Sync modal state with userInput changes (e.g., after switching standard via "(byt)" button)
  // This ensures the modal closes when the standard is changed via the header "(byt)" button
  // Using useLayoutEffect to run synchronously before paint, preventing modal flash
  useLayoutEffect(() => {
    const confirmed = !!(report.userInput as Record<string, unknown>)?.accountingStandardConfirmed;
    if (confirmed || standardJustConfirmed) {
      setShowAccountingStandardSelector(false);
    }
  }, [report.userInput, standardJustConfirmed]);

  // Initialize flerårsöversikt - combines historical data from DB with SIE data
  const getInitialPreviousYears = (): PreviousYearEntry[] => {
    // If user already has saved data, use that
    if (userInput.previousYears && userInput.previousYears.length > 0) {
      return userInput.previousYears;
    }
    // Otherwise start empty (will be populated in useEffect)
    return [];
  };

  const [previousYears, setPreviousYears] = useState<PreviousYearEntry[]>(getInitialPreviousYears);
  previousYearsRef.current = previousYears;
  const [hasAutoPopulated, setHasAutoPopulated] = useState(false);

  // Equity changes state (Förändringar i eget kapital)
  const [equityChanges, setEquityChanges] = useState<EquityChangesInput>(
    userInput.equityChanges || {}
  );
  const equityChangesRef = useRef(equityChanges);
  equityChangesRef.current = equityChanges;
  const [lastSavedEquityChanges, setLastSavedEquityChanges] = useState<EquityChangesInput>(
    userInput.equityChanges || {}
  );

  // Helper to check if equityChanges has any actual values
  const hasEquityChangesValues = useCallback((changes: EquityChangesInput | undefined): boolean => {
    if (!changes) return false;
    const hasShareIssue = changes.shareIssue?.totalShareCapitalIncrease && changes.shareIssue.totalShareCapitalIncrease > 0;
    const hasShareholderContributions = changes.shareholderContributions?.some(c => c.amount && c.amount > 0);
    const hasDividend = changes.dividendPaidDuringYear && changes.dividendPaidDuringYear > 0;
    const hasGroupContributionReceived = changes.groupContributionReceived && changes.groupContributionReceived > 0;
    const hasGroupContributionGiven = changes.groupContributionGiven && changes.groupContributionGiven > 0;
    const hasBonusIssue = changes.bonusIssue?.amount && changes.bonusIssue.amount > 0;
    const hasCapitalReduction = changes.capitalReduction?.amount && changes.capitalReduction.amount > 0;
    const hasReserveFundTransfers = changes.reserveFundTransfers?.some(t => t.amount && t.amount > 0);
    const hasOtherChanges = changes.otherChanges?.some(c => c.amount && c.amount !== 0);
    const hasContributionRepayments = changes.contributionRepayments?.some(r => r.amount && r.amount > 0);
    return !!(hasShareIssue || hasShareholderContributions || hasDividend ||
              hasGroupContributionReceived || hasGroupContributionGiven ||
              hasBonusIssue || hasCapitalReduction || hasReserveFundTransfers ||
              hasOtherChanges || hasContributionRepayments);
  }, []);

  // Auto-expand section if there are saved values
  const [showEquityChangesSection, setShowEquityChangesSection] = useState(
    () => hasEquityChangesValues(userInput.equityChanges)
  );

  // Sync equityChanges state when userInput.equityChanges changes (e.g., when navigating back to this step)
  // This ensures that saved values are loaded into the local state
  // We use JSON.stringify comparison to avoid infinite loops from state updates
  const userInputEquityChangesStr = JSON.stringify(userInput.equityChanges || {});
  useEffect(() => {
    const newEquityChanges = userInput.equityChanges || {};
    const currentStr = JSON.stringify(equityChanges);
    const newStr = JSON.stringify(newEquityChanges);

    // Only sync if the incoming data is different from current state
    // This prevents overwriting user's in-progress edits
    if (currentStr !== newStr && newStr !== '{}') {
      setEquityChanges(newEquityChanges);
      setLastSavedEquityChanges(newEquityChanges);
      // Also expand section if there are values
      if (hasEquityChangesValues(newEquityChanges)) {
        setShowEquityChangesSection(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInputEquityChangesStr]); // Only re-run when userInput.equityChanges actually changes

  // Pre-fill equity changes from SIE-detected changes when no manual values exist
  // This helps users by automatically populating detected changes from the SIE file
  // Using ref instead of state to prevent double-execution in React StrictMode
  const hasPrefilledFromSieRef = useRef(false);
  useEffect(() => {
    // Only run once, and only if:
    // 1. We have SIE data with equity analysis
    // 2. There are no saved manual equity changes
    // 3. SIE has detected actual changes
    if (hasPrefilledFromSieRef.current || !sieData?.equityChangeAnalysis) return;
    const eqAnalysis = sieData.equityChangeAnalysis;

    // Check if there are any detected changes
    if (!eqAnalysis.summary.hasAnyChanges) return;

    // Check if userInput already has values - don't overwrite
    if (hasEquityChangesValues(userInput.equityChanges)) return;

    // Pre-fill from SIE-detected changes
    const prefilled: EquityChangesInput = {};

    // Check for share issue (nyemission)
    const shareIssueChange = eqAnalysis.shareCapital.changes.find(c => c.type === 'share_issue');
    if (shareIssueChange && shareIssueChange.amount > 0) {
      // Also check for överkursfond increase
      const premiumChange = eqAnalysis.sharePremium.changes.find(c => c.type === 'share_issue');
      prefilled.shareIssue = {
        totalShareCapitalIncrease: shareIssueChange.amount,
        sharePremiumAmount: premiumChange?.amount || 0,
        type: 'cash',
      };
    }

    // Check for shareholder contributions (aktieägartillskott)
    const contributionChanges = eqAnalysis.shareholderContributions.changes.filter(
      c => c.type === 'shareholder_contribution' && c.amount > 0
    );
    if (contributionChanges.length > 0) {
      prefilled.shareholderContributions = contributionChanges.map(c => ({
        amount: c.amount,
        type: 'unconditional' as const,
        contributor: '',
      }));
    }

    // Check for dividend paid (utbetald utdelning)
    const dividendChange = eqAnalysis.retainedEarnings.changes.find(c => c.type === 'dividend_paid');
    if (dividendChange && dividendChange.amount < 0) {
      prefilled.dividendPaidDuringYear = Math.abs(dividendChange.amount);
    }

    // Check for group contributions
    const groupReceivedChange = eqAnalysis.groupContributions.changes.find(
      c => c.type === 'group_contribution_received'
    );
    if (groupReceivedChange && groupReceivedChange.amount > 0) {
      prefilled.groupContributionReceived = groupReceivedChange.amount;
    }

    const groupGivenChange = eqAnalysis.groupContributions.changes.find(
      c => c.type === 'group_contribution_given'
    );
    if (groupGivenChange && groupGivenChange.amount < 0) {
      prefilled.groupContributionGiven = Math.abs(groupGivenChange.amount);
    }

    // Check for bonus issue (fondemission)
    const bonusChange = eqAnalysis.shareCapital.changes.find(c => c.type === 'bonus_issue');
    if (bonusChange && bonusChange.amount > 0) {
      // Default to sharePremium as source - user can change if needed
      prefilled.bonusIssue = {
        sourceAccount: 'sharePremium',
        amount: bonusChange.amount,
      };
    }

    // Check for capital reduction (kapitalnedsättning)
    const capitalReductionChange = eqAnalysis.shareCapital.changes.find(c => c.type === 'capital_reduction');
    if (capitalReductionChange && capitalReductionChange.amount < 0) {
      // Default to loss_coverage as purpose - user can change if needed
      prefilled.capitalReduction = {
        amount: Math.abs(capitalReductionChange.amount),
        purpose: 'loss_coverage',
      };
    }

    // Only apply if we found anything
    if (Object.keys(prefilled).length > 0) {
      // Set ref FIRST (synchronously) to prevent double-execution in StrictMode
      hasPrefilledFromSieRef.current = true;
      setEquityChanges(prefilled);
      setShowEquityChangesSection(true);
      toast.info('Förändringar i eget kapital har automatiskt fyllts i från SIE-data', { duration: 5000 });
    }
  }, [sieData, userInput.equityChanges, hasEquityChangesValues]);

  // Auto-populate flerårsöversikt from SIE data on first load
  // This combines current year SIE data with any historical data from DB
  useEffect(() => {
    if (!hasAutoPopulated && sieData) {
      const currentYear = sieData.metadata.fiscalYearEnd.split("-")[0];

      // Start with existing entries (historical data from DB via userInput)
      const existingYears = new Set(previousYears.map(e => e.year));
      const newEntries: PreviousYearEntry[] = [];

      // Always add/update current year from SIE (most recent data)
      const currentYearEntry: PreviousYearEntry = {
        year: currentYear,
        netSales: sieData.incomeStatement.revenue.netSales,
        resultAfterFinancialItems: sieData.incomeStatement.resultAfterFinancialItems,
        solidityPercent: Math.round(sieData.keyFigures.solidityPercent * 10) / 10,
      };
      newEntries.push(currentYearEntry);
      existingYears.add(currentYear);

      // Add previous year from SIE if available and not already in historical data
      if (sieData.previousYearKeyFigures && sieData.previousYearIncomeStatement) {
        const prevYear = (parseInt(currentYear) - 1).toString();
        if (!existingYears.has(prevYear)) {
          newEntries.push({
            year: prevYear,
            netSales: sieData.previousYearKeyFigures.netSales,
            resultAfterFinancialItems: sieData.previousYearIncomeStatement.resultAfterFinancialItems,
            solidityPercent: Math.round(sieData.previousYearKeyFigures.solidityPercent * 10) / 10,
          });
        }
      }

      // Add historical data from DB (already in previousYears)
      // but skip current year as we just added fresh SIE data for it
      for (const entry of previousYears) {
        if (entry.year !== currentYear) {
          newEntries.push(entry);
        }
      }

      // Sort by year descending and limit to 4 years
      const sortedEntries = newEntries
        .sort((a, b) => parseInt(b.year) - parseInt(a.year))
        .slice(0, 4);

      if (sortedEntries.length > 0) {
        setPreviousYears(sortedEntries);
      }
      setHasAutoPopulated(true);
    }
  }, [sieData, hasAutoPopulated, previousYears]);

  // Check for unsaved changes (compare against last saved state, not userInput)
  useEffect(() => {
    const hasOverrideChanges =
      JSON.stringify(overrides) !== JSON.stringify(lastSavedOverrides);
    const hasPreviousYearsChanges =
      JSON.stringify(previousYears) !== JSON.stringify(lastSavedPreviousYears);
    const hasEquityChangesChanges =
      JSON.stringify(equityChanges) !== JSON.stringify(lastSavedEquityChanges);
    setHasUnsavedChanges(hasOverrideChanges || hasPreviousYearsChanges || hasEquityChangesChanges);
  }, [overrides, previousYears, equityChanges, lastSavedOverrides, lastSavedPreviousYears, lastSavedEquityChanges]);

  // Auto-save with debounce (uses refs for latest values to avoid stale closures)
  useEffect(() => {
    if (!hasUnsavedChanges || isSaving) return;

    const timeoutId = setTimeout(async () => {
      // Capture current values from refs at save time
      const currentOverrides = overridesRef.current;
      const currentPreviousYears = previousYearsRef.current;
      const currentEquityChanges = equityChangesRef.current;
      const currentUserInput = userInputRef.current;

      setIsSaving(true);
      setSaveError(null);
      try {
        const saveData = {
          ...currentUserInput,
          overrides: currentOverrides,
          previousYears: currentPreviousYears,
          equityChanges: currentEquityChanges,
        };

        if (onSaveInput) {
          // Guest mode: use provided save handler
          const result = await onSaveInput(saveData);
          if (result.success) {
            setLastSavedOverrides(currentOverrides);
            setLastSavedPreviousYears(currentPreviousYears);
            setLastSavedEquityChanges(currentEquityChanges);
            if (result.report) onUpdate(result.report);
          } else {
            setSaveError(result.error || "Kunde inte spara ändringar");
          }
        } else {
          // Authenticated mode: use API
          const response = await reportsApi.updateInput(report.id, saveData);
          if (response.success && response.data) {
            setLastSavedOverrides(currentOverrides);
            setLastSavedPreviousYears(currentPreviousYears);
            setLastSavedEquityChanges(currentEquityChanges);
            onUpdate(response.data.report);
          } else if (response.error) {
            setSaveError(response.error.message || "Kunde inte spara ändringar");
          }
        }
      } catch (err) {
        console.error("Auto-save error:", err);
        toast.error("Kunde inte spara ändringar");
        setSaveError("Kunde inte spara ändringar. Kontrollera din anslutning.");
      } finally {
        setIsSaving(false);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [overrides, previousYears, equityChanges, hasUnsavedChanges, isSaving]);

  if (!sieData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Ingen SIE-data hittades.</p>
        <button
          onClick={() => onNavigateStep ? onNavigateStep(prevStep) : navigate(`/dashboard/wizard/${report.id}/step/${prevStep}`)}
          className="mt-4 btn btn-primary"
        >
          Gå tillbaka och ladda upp
        </button>
      </div>
    );
  }

  const { balanceSheet: originalBS, incomeStatement: originalIS } = sieData;

  // Previous year data (jämförelsetal)
  const prevBS = sieData.previousYearBalanceSheet;
  const prevIS = sieData.previousYearIncomeStatement;
  const prevKF = sieData.previousYearKeyFigures;
  const hasPreviousYear = prevBS !== null || prevIS !== null;

  // Fiscal year dates for column headers
  const currentYearEnd = sieData.metadata.fiscalYearEnd; // e.g., "2024-12-31"
  const currentYearStart = sieData.metadata.fiscalYearStart; // e.g., "2024-01-01"

  // Calculate previous year dates (subtract 1 year from current)
  const getPreviousYearDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split("T")[0];
  };
  const previousYearEnd = getPreviousYearDate(currentYearEnd);
  const previousYearStart = getPreviousYearDate(currentYearStart);

  // Format fiscal year period for display (two lines)
  const formatPeriod = (start: string, end: string) => ({ start, end })
  const currentYearPeriod = formatPeriod(currentYearStart, currentYearEnd);
  const previousYearPeriod = formatPeriod(previousYearStart, previousYearEnd);

  // Calculate effective values (original + overrides)
  const getEffectiveValue = (
    section: "incomeStatement" | "balanceSheet",
    path: string,
    originalValue: number
  ): number => {
    const overridePath = `${section}.${path}`;
    const overrideValue = getNestedValue(overrides, overridePath);
    return overrideValue !== undefined ? overrideValue : originalValue;
  };

  const handleOverride = (
    section: "incomeStatement" | "balanceSheet",
    path: string,
    value: number
  ) => {
    const fullPath = `${section}.${path}`;
    setOverrides((prev) => setNestedValue(prev, fullPath, value));
  };

  const handleReset = (
    section: "incomeStatement" | "balanceSheet",
    path: string
  ) => {
    const fullPath = `${section}.${path}`;
    setOverrides((prev) => removeNestedValue(prev, fullPath));
  };

  const handleResetAll = () => {
    setOverrides({});
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // Toggle all sections within a block (Resultaträkning or Balansräkning)
  const toggleAllSectionsInBlock = (sections: string[]) => {
    setExpandedSections((prev) => {
      // Check if all sections in this block are currently expanded
      const allExpanded = sections.every((s) => prev.has(s));
      const next = new Set(prev);

      if (allExpanded) {
        // Collapse all in this block
        sections.forEach((s) => next.delete(s));
      } else {
        // Expand all in this block
        sections.forEach((s) => next.add(s));
      }
      return next;
    });
  };

  // Check if all sections in a block are expanded
  const areAllSectionsExpanded = (sections: string[]) => {
    return sections.every((s) => expandedSections.has(s));
  };

  // Calculate effective income statement values
  const effectiveIS = {
    revenue: {
      netSales: getEffectiveValue(
        "incomeStatement",
        "revenue.netSales",
        originalIS.revenue.netSales
      ),
      otherIncome: getEffectiveValue(
        "incomeStatement",
        "revenue.otherIncome",
        originalIS.revenue.otherIncome
      ),
      get total() {
        return this.netSales + this.otherIncome;
      },
    },
    expenses: {
      goodsAndMaterials: getEffectiveValue(
        "incomeStatement",
        "expenses.goodsAndMaterials",
        originalIS.expenses.goodsAndMaterials
      ),
      otherExternalCosts: getEffectiveValue(
        "incomeStatement",
        "expenses.otherExternalCosts",
        originalIS.expenses.otherExternalCosts
      ),
      personnelCosts: getEffectiveValue(
        "incomeStatement",
        "expenses.personnelCosts",
        originalIS.expenses.personnelCosts
      ),
      depreciationAmortization: getEffectiveValue(
        "incomeStatement",
        "expenses.depreciationAmortization",
        originalIS.expenses.depreciationAmortization
      ),
      otherOperatingExpenses: getEffectiveValue(
        "incomeStatement",
        "expenses.otherOperatingExpenses",
        originalIS.expenses.otherOperatingExpenses
      ),
      get total() {
        return (
          this.goodsAndMaterials +
          this.otherExternalCosts +
          this.personnelCosts +
          this.depreciationAmortization +
          this.otherOperatingExpenses
        );
      },
    },
    financialItems: {
      financialIncome: getEffectiveValue(
        "incomeStatement",
        "financialItems.financialIncome",
        originalIS.financialItems.financialIncome
      ),
      financialExpenses: getEffectiveValue(
        "incomeStatement",
        "financialItems.financialExpenses",
        originalIS.financialItems.financialExpenses
      ),
      get total() {
        return this.financialIncome - this.financialExpenses;
      },
    },
    extraordinaryItems: getEffectiveValue(
      "incomeStatement",
      "extraordinaryItems",
      originalIS.extraordinaryItems
    ),
    appropriations: getEffectiveValue(
      "incomeStatement",
      "appropriations",
      originalIS.appropriations
    ),
    taxOnResult: getEffectiveValue(
      "incomeStatement",
      "taxOnResult",
      originalIS.taxOnResult
    ),
    get operatingResult() {
      return this.revenue.total - this.expenses.total;
    },
    get resultAfterFinancialItems() {
      return this.operatingResult + this.financialItems.total;
    },
    get netResult() {
      return (
        this.resultAfterFinancialItems +
        this.extraordinaryItems +
        this.appropriations -
        this.taxOnResult
      );
    },
  };

  // Check if manual entry mode (for showing separate buildings/machinery fields)
  const isManualEntryMode = isManualEntry(sieData);

  // Calculate effective balance sheet values
  const effectiveBS = {
    assets: {
      fixedAssets: {
        intangible: getEffectiveValue(
          "balanceSheet",
          "assets.fixedAssets.intangible",
          originalBS.assets.fixedAssets.intangible
        ),
        // For manual entry, support separate buildings and machinery
        buildings: getEffectiveValue(
          "balanceSheet",
          "assets.fixedAssets.buildings",
          originalBS.assets.fixedAssets.buildings ?? 0
        ),
        machinery: getEffectiveValue(
          "balanceSheet",
          "assets.fixedAssets.machinery",
          originalBS.assets.fixedAssets.machinery ?? 0
        ),
        // tangible can be overridden directly (SIE mode) or calculated from buildings+machinery (manual mode)
        get tangible() {
          const overriddenTangible = getNestedValue(overrides, "balanceSheet.assets.fixedAssets.tangible");
          if (overriddenTangible !== undefined) {
            return overriddenTangible as number;
          }
          // In manual mode, tangible = buildings + machinery
          if (isManualEntryMode) {
            return this.buildings + this.machinery;
          }
          return originalBS.assets.fixedAssets.tangible;
        },
        financial: getEffectiveValue(
          "balanceSheet",
          "assets.fixedAssets.financial",
          originalBS.assets.fixedAssets.financial
        ),
        get total() {
          return this.intangible + this.tangible + this.financial;
        },
      },
      currentAssets: {
        inventory: getEffectiveValue(
          "balanceSheet",
          "assets.currentAssets.inventory",
          originalBS.assets.currentAssets.inventory
        ),
        receivables: getEffectiveValue(
          "balanceSheet",
          "assets.currentAssets.receivables",
          originalBS.assets.currentAssets.receivables
        ),
        shortTermInvestments: getEffectiveValue(
          "balanceSheet",
          "assets.currentAssets.shortTermInvestments",
          originalBS.assets.currentAssets.shortTermInvestments
        ),
        cashAndBank: getEffectiveValue(
          "balanceSheet",
          "assets.currentAssets.cashAndBank",
          originalBS.assets.currentAssets.cashAndBank
        ),
        get total() {
          return (
            this.inventory +
            this.receivables +
            this.shortTermInvestments +
            this.cashAndBank
          );
        },
      },
      get totalAssets() {
        return this.fixedAssets.total + this.currentAssets.total;
      },
    },
    equityAndLiabilities: {
      equity: {
        // Bundet eget kapital
        shareCapital: getEffectiveValue(
          "balanceSheet",
          "equityAndLiabilities.equity.shareCapital",
          originalBS.equityAndLiabilities.equity.shareCapital
        ),
        unregisteredShareCapital: getEffectiveValue(
          "balanceSheet",
          "equityAndLiabilities.equity.unregisteredShareCapital",
          originalBS.equityAndLiabilities.equity.unregisteredShareCapital ?? 0
        ),
        // 2084 - Uppskrivningsfond (bundet EK)
        revaluationReserve: getEffectiveValue(
          "balanceSheet",
          "equityAndLiabilities.equity.revaluationReserve",
          originalBS.equityAndLiabilities.equity.revaluationReserve
        ),
        legalReserve: getEffectiveValue(
          "balanceSheet",
          "equityAndLiabilities.equity.legalReserve",
          originalBS.equityAndLiabilities.equity.legalReserve
        ),
        developmentFund: getEffectiveValue(
          "balanceSheet",
          "equityAndLiabilities.equity.developmentFund",
          originalBS.equityAndLiabilities.equity.developmentFund ?? 0
        ),
        // Fritt eget kapital
        // 2085 + 2097 - Överkursfond (fritt EK)
        sharePremium: getEffectiveValue(
          "balanceSheet",
          "equityAndLiabilities.equity.sharePremium",
          originalBS.equityAndLiabilities.equity.sharePremium ?? 0
        ),
        retainedEarnings: getEffectiveValue(
          "balanceSheet",
          "equityAndLiabilities.equity.retainedEarnings",
          originalBS.equityAndLiabilities.equity.retainedEarnings
        ),
        // Detaljerade komponenter (för förändring av eget kapital) - nu redigerbara
        details: {
          retainedEarningsOnly: getEffectiveValue(
            "balanceSheet",
            "equityAndLiabilities.equity.details.retainedEarningsOnly",
            originalBS.equityAndLiabilities.equity.details?.retainedEarningsOnly ?? 0
          ),
          groupContributions: getEffectiveValue(
            "balanceSheet",
            "equityAndLiabilities.equity.details.groupContributions",
            originalBS.equityAndLiabilities.equity.details?.groupContributions ?? 0
          ),
          shareholderContributions: getEffectiveValue(
            "balanceSheet",
            "equityAndLiabilities.equity.details.shareholderContributions",
            originalBS.equityAndLiabilities.equity.details?.shareholderContributions ?? 0
          ),
          ownShares: getEffectiveValue(
            "balanceSheet",
            "equityAndLiabilities.equity.details.ownShares",
            originalBS.equityAndLiabilities.equity.details?.ownShares ?? 0
          ),
          mergerResult: getEffectiveValue(
            "balanceSheet",
            "equityAndLiabilities.equity.details.mergerResult",
            originalBS.equityAndLiabilities.equity.details?.mergerResult ?? 0
          ),
          prevYearProfitLoss: getEffectiveValue(
            "balanceSheet",
            "equityAndLiabilities.equity.details.prevYearProfitLoss",
            originalBS.equityAndLiabilities.equity.details?.prevYearProfitLoss ?? 0
          ),
          sharePremium2085: originalBS.equityAndLiabilities.equity.details?.sharePremium2085 ?? 0,
          sharePremiumOld2097: originalBS.equityAndLiabilities.equity.details?.sharePremiumOld2097 ?? 0,
        },
        // Current year result comes from income statement
        get currentYearResult() {
          return effectiveIS.netResult;
        },
        get total() {
          return (
            this.shareCapital +
            this.unregisteredShareCapital +
            this.revaluationReserve +
            this.legalReserve +
            this.developmentFund +
            this.sharePremium +
            this.retainedEarnings +
            this.currentYearResult
          );
        },
      },
      untaxedReserves: getEffectiveValue(
        "balanceSheet",
        "equityAndLiabilities.untaxedReserves",
        originalBS.equityAndLiabilities.untaxedReserves
      ),
      provisions: getEffectiveValue(
        "balanceSheet",
        "equityAndLiabilities.provisions",
        originalBS.equityAndLiabilities.provisions
      ),
      longTermLiabilities: getEffectiveValue(
        "balanceSheet",
        "equityAndLiabilities.longTermLiabilities",
        originalBS.equityAndLiabilities.longTermLiabilities
      ),
      shortTermLiabilities: getEffectiveValue(
        "balanceSheet",
        "equityAndLiabilities.shortTermLiabilities",
        originalBS.equityAndLiabilities.shortTermLiabilities
      ),
      get totalEquityAndLiabilities() {
        return (
          this.equity.total +
          this.untaxedReserves +
          this.provisions +
          this.longTermLiabilities +
          this.shortTermLiabilities
        );
      },
    },
  };

  // Calculate key figures
  const TAX_RATE = 0.206;
  const effectiveKeyFigures = {
    netSales: effectiveIS.revenue.netSales,
    netResult: effectiveIS.netResult,
    totalAssets: effectiveBS.assets.totalAssets,
    equity: effectiveBS.equityAndLiabilities.equity.total,
    adjustedEquity:
      effectiveBS.equityAndLiabilities.equity.total +
      effectiveBS.equityAndLiabilities.untaxedReserves * (1 - TAX_RATE),
    get solidityPercent() {
      return this.totalAssets > 0
        ? (this.adjustedEquity / this.totalAssets) * 100
        : 0;
    },
    get freeEquity() {
      const eq = effectiveBS.equityAndLiabilities.equity;
      // Fritt EK = Totalt EK - Bundet EK (aktiekapital + ej reg AK + uppskrivningsfond + reservfond + utvecklingsfond)
      return eq.total - eq.shareCapital - eq.unregisteredShareCapital -
             eq.revaluationReserve - eq.legalReserve - eq.developmentFund;
    },
  };

  // Count overrides
  const overrideCount = Object.keys(overrides.incomeStatement || {}).length +
    Object.keys(overrides.balanceSheet || {}).length;

  // K2 2026 eligibility check - fiscal year starting 2026-01-01 or later
  // Note: K2 eligibility questions are now answered in Step 1, so we only need to check the saved values here
  const fiscalYearStart = sieData?.metadata?.fiscalYearStart || "";
  const isFiscalYear2026OrLater = (() => {
    if (!fiscalYearStart) return false;
    const startDate = new Date(fiscalYearStart);
    const cutoffDate = new Date("2026-01-01");
    return startDate >= cutoffDate;
  })();

  const k2EligibilityConfirmed = !!userInput.k2Eligibility?.confirmedAt;
  const k2EligibilityPasses =
    !userInput.k2Eligibility?.hasForeignBranches &&
    !userInput.k2Eligibility?.hasCryptoAssets &&
    !userInput.k2Eligibility?.hasShareBasedPayments;

  // Real-time validation based on effective values
  type ValidationIssue = { type: "blocker" | "warning" | "info"; code: string; message: string; legalRef?: string };

  const calculateRealTimeValidation = (): ValidationIssue[] => {
    const issues: ValidationIssue[] = [];

    // BLOCKER: Balance equation check
    const totalAssets = effectiveBS.assets.totalAssets;
    const totalEquityAndLiabilities = effectiveBS.equityAndLiabilities.totalEquityAndLiabilities;
    const balanceDiff = Math.abs(totalAssets - totalEquityAndLiabilities);

    if (balanceDiff > 1) {
      issues.push({
        type: "blocker",
        code: "BALANCE_EQUATION",
        message: `Balansräkningen går inte ihop. Tillgångar (${formatNumber(totalAssets)}) ≠ EK + Skulder (${formatNumber(totalEquityAndLiabilities)}). Differens: ${formatNumber(balanceDiff)} kr`,
        legalRef: "ÅRL 3 kap",
      });
    }

    // BLOCKER: Result mismatch check (RR result vs BR result)
    const rrResult = effectiveIS.netResult;
    const brResult = effectiveBS.equityAndLiabilities.equity.currentYearResult;
    const resultDiff = Math.abs(rrResult - brResult);

    if (resultDiff > 1) {
      issues.push({
        type: "blocker",
        code: "RESULT_MISMATCH",
        message: `Årets resultat i RR (${formatNumber(rrResult)}) matchar inte BR (${formatNumber(brResult)}). Differens: ${formatNumber(resultDiff)} kr`,
        legalRef: "ÅRL",
      });
    }

    // BLOCKER: Negative share capital (bookkeeping error)
    const shareCapital = effectiveBS.equityAndLiabilities.equity.shareCapital;
    if (shareCapital < 0) {
      issues.push({
        type: "blocker",
        code: "NEGATIVE_SHARE_CAPITAL",
        message: `Aktiekapitalet är negativt (${formatNumber(shareCapital)} kr). Detta indikerar ett bokföringsfel på konto 2081.`,
        legalRef: "ABL 1 kap 5§",
      });
    }

    // BLOCKER: Minimum share capital
    if (shareCapital >= 0 && shareCapital < 25000) {
      issues.push({
        type: "blocker",
        code: "SHARE_CAPITAL_MINIMUM",
        message: `Aktiekapitalet (${formatNumber(shareCapital)} kr) är under minsta tillåtna 25 000 kr`,
        legalRef: "ABL 1 kap 5§",
      });
    }

    // BLOCKER: K2 2026 eligibility - must be confirmed for fiscal years starting 2026+
    // Note: This should normally not trigger since K2 questions are now in Step 1.
    // This serves as a fallback in case user bypasses Step 1 somehow.
    if (isFiscalYear2026OrLater && !k2EligibilityConfirmed) {
      issues.push({
        type: "blocker",
        code: "K2_2026_NOT_CONFIRMED",
        message: "K2-behörighet måste bekräftas för räkenskapsår som börjar 2026-01-01 eller senare. Gå tillbaka till Steg 1 för att besvara K2-behörighetsfrågorna.",
        legalRef: "BFNAR 2016:10 punkt 1.1A",
      });
    }

    // BLOCKER: K2 2026 eligibility - fails criteria
    if (isFiscalYear2026OrLater && k2EligibilityConfirmed && !k2EligibilityPasses) {
      if (userInput.k2Eligibility?.hasForeignBranches) {
        issues.push({
          type: "blocker",
          code: "K2_FOREIGN_BRANCHES",
          message: "Företag med filialer i utlandet får inte tillämpa K2 från räkenskapsår som börjar 2026-01-01 eller senare",
          legalRef: "BFNAR 2016:10 punkt 1.1A",
        });
      }
      if (userInput.k2Eligibility?.hasCryptoAssets) {
        issues.push({
          type: "blocker",
          code: "K2_CRYPTO_ASSETS",
          message: "Företag med kryptotillgångar får inte tillämpa K2 från räkenskapsår som börjar 2026-01-01 eller senare",
          legalRef: "BFNAR 2016:10 punkt 1.1A",
        });
      }
      if (userInput.k2Eligibility?.hasShareBasedPayments) {
        issues.push({
          type: "blocker",
          code: "K2_SHARE_BASED_PAYMENTS",
          message: "Företag med aktierelaterade ersättningar får inte tillämpa K2 från räkenskapsår som börjar 2026-01-01 eller senare",
          legalRef: "BFNAR 2016:10 punkt 1.1A",
        });
      }
    }

    // WARNING: Low equity ratio
    if (effectiveKeyFigures.solidityPercent < 20 && effectiveKeyFigures.solidityPercent >= 0) {
      issues.push({
        type: "warning",
        code: "LOW_EQUITY_RATIO",
        message: `Låg soliditet (${effectiveKeyFigures.solidityPercent.toFixed(1)}%). Soliditet under 20% kan vara en varningssignal.`,
      });
    }

    // WARNING: Equity below half of share capital - critical ABL requirement
    const equity = effectiveBS.equityAndLiabilities.equity.total;
    if (equity < shareCapital / 2 && equity >= 0) {
      issues.push({
        type: "warning",
        code: "EQUITY_BELOW_HALF_SHARE_CAPITAL",
        message: `Kapitalbrist: Eget kapital (${formatNumber(equity)} kr) understiger hälften av aktiekapitalet (${formatNumber(shareCapital / 2)} kr). Styrelsen ska genast upprätta kontrollbalansräkning.`,
        legalRef: "ABL 25 kap 13§",
      });
    }

    // WARNING: Negative equity
    if (equity < 0) {
      issues.push({
        type: "warning",
        code: "NEGATIVE_EQUITY",
        message: `Negativt eget kapital (${formatNumber(equity)} kr). Detta är en allvarlig varningssignal.`,
      });
    }

    // INFO: No previous year data (keep from original)
    const hasPrevYearData = sieData.previousYearBalanceSheet !== null || sieData.previousYearIncomeStatement !== null;
    if (!hasPrevYearData) {
      const isManual = isManualEntry(sieData);
      issues.push({
        type: "info",
        code: "NO_PREVIOUS_YEAR_DATA",
        message: isManual
          ? "Jämförelsetal för föregående år saknas. Om detta är första räkenskapsåret är det normalt. Du kan lägga till jämförelsetal i Flerårsöversikten nedan."
          : "SIE-filen innehåller inga jämförelsetal för föregående år. Om detta är första räkenskapsåret är det normalt.",
        legalRef: "ÅRL 3 kap 5§",
      });
    }

    return issues;
  };

  // Use real-time validation instead of static sieData.validationIssues
  const realTimeIssues = calculateRealTimeValidation();
  const blockers = realTimeIssues.filter((i) => i.type === "blocker");
  const warnings = realTimeIssues.filter((i) => i.type === "warning");
  const infos = realTimeIssues.filter((i) => i.type === "info");
  const hasBlockers = blockers.length > 0;

  // Navigate to next step
  const handleContinue = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const saveData = {
        ...userInput,
        overrides,
        previousYears,
        equityChanges,
        currentStep: 3,
      };

      if (onSaveInput) {
        // Guest mode
        const result = await onSaveInput(saveData);
        if (result.success) {
          if (result.report) onUpdate(result.report);
          setHasUnsavedChanges(false);
          if (onNavigateStep) onNavigateStep(nextStep);
          else navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
        } else {
          setSaveError(result.error || "Kunde inte spara. Försök igen.");
        }
      } else {
        // Authenticated mode
        const response = await reportsApi.updateInput(report.id, saveData);
        if (response.success && response.data) {
          onUpdate(response.data.report);
          setHasUnsavedChanges(false);
          if (onNavigateStep) onNavigateStep(nextStep);
          else navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
        } else if (response.error) {
          setSaveError(response.error.message || "Kunde inte spara. Försök igen.");
        }
      }
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Kunde inte spara");
      setSaveError("Kunde inte spara. Kontrollera din anslutning och försök igen.");
    } finally {
      setIsSaving(false);
    }
  };

  // Flerårsöversikt handlers
  const addManualYear = () => {
    const currentYear = parseInt(currentYearEnd.split("-")[0]);
    // Find the earliest year we already have
    const existingYears = previousYears.map(y => parseInt(y.year));
    const nextYear = existingYears.length > 0
      ? Math.min(...existingYears) - 1
      : currentYear - 1;

    setPreviousYears([
      ...previousYears,
      { year: nextYear.toString(), netSales: 0, resultAfterFinancialItems: 0, solidityPercent: 0 }
    ]);
  };

  const removeYear = (year: string) => {
    setPreviousYears(previousYears.filter(y => y.year !== year));
  };

  const updateYearData = (year: string, field: keyof PreviousYearEntry, value: number | string) => {
    setPreviousYears(previousYears.map(y =>
      y.year === year ? { ...y, [field]: value } : y
    ));
  };

  const fillFromHistory = () => {
    // Update from current effective values (reflects any user overrides)
    const currentYear = currentYearEnd.split("-")[0];

    // Update current year entry with effective values
    const updatedEntries = previousYears.map(entry => {
      if (entry.year === currentYear) {
        return {
          ...entry,
          netSales: effectiveKeyFigures.netSales,
          resultAfterFinancialItems: effectiveIS.resultAfterFinancialItems,
          solidityPercent: Math.round(effectiveKeyFigures.solidityPercent * 10) / 10,
        };
      }
      return entry;
    });

    // If current year doesn't exist, add it
    if (!updatedEntries.find(e => e.year === currentYear)) {
      updatedEntries.unshift({
        year: currentYear,
        netSales: effectiveKeyFigures.netSales,
        resultAfterFinancialItems: effectiveIS.resultAfterFinancialItems,
        solidityPercent: Math.round(effectiveKeyFigures.solidityPercent * 10) / 10,
      });
    }

    setPreviousYears(updatedEntries);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Granska & Validera
        </h2>
        <p className="text-gray-600">
          Klicka på siffror för att redigera. Expandera sektioner för detaljer.
        </p>
      </div>


      {/* Warnings and Info - shown at top */}
      {(warnings.length > 0 || infos.length > 0) && (
        <div className="space-y-3">
          {/* Critical warning: Kontrollbalansräkning */}
          {warnings.some(w => w.code === "EQUITY_BELOW_HALF_SHARE_CAPITAL") && (
            <div className="bg-amber-100 border-2 border-amber-400 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-200 rounded-full">
                  <AlertTriangle className="text-amber-700" size={24} />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-900">
                    Varning: Kapitalbrist
                  </h4>
                  <p className="text-sm text-amber-800 mt-1">
                    Eget kapital ({formatNumber(effectiveBS.equityAndLiabilities.equity.total)} kr)
                    understiger hälften av aktiekapitalet ({formatNumber(effectiveBS.equityAndLiabilities.equity.shareCapital / 2)} kr).
                    Enligt ABL 25 kap 13§ ska styrelsen <strong>genast</strong> upprätta kontrollbalansräkning.
                  </p>
                  <button
                    onClick={() => setShowKontrollbalansModal(true)}
                    className="mt-3 inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-900 bg-amber-200 hover:bg-amber-300 rounded-lg transition-colors"
                  >
                    Läs mer om kontrollbalansräkning
                    <ArrowRight size={16} className="ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Other warnings */}
          {warnings.filter(w => w.code !== "EQUITY_BELOW_HALF_SHARE_CAPITAL").length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="text-amber-600 flex-shrink-0" size={18} />
                <div className="flex-1">
                  <span className="font-medium text-amber-800 text-sm">
                    {warnings.filter(w => w.code !== "EQUITY_BELOW_HALF_SHARE_CAPITAL").length} varning
                    {warnings.filter(w => w.code !== "EQUITY_BELOW_HALF_SHARE_CAPITAL").length > 1 ? "ar" : ""}:
                  </span>
                  <span className="text-sm text-amber-700 ml-2">
                    {warnings.filter(w => w.code !== "EQUITY_BELOW_HALF_SHARE_CAPITAL").map(w => w.message).join(" • ")}
                  </span>
                </div>
              </div>
            </div>
          )}

          {infos.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Info className="text-blue-600 flex-shrink-0" size={18} />
                <span className="text-sm text-blue-700">
                  {infos.map(i => i.message).join(" • ")}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Kontrollbalansräkning Info Modal */}
      <KontrollbalansrakningInfoModal
        isOpen={showKontrollbalansModal}
        onClose={() => setShowKontrollbalansModal(false)}
        equity={effectiveBS.equityAndLiabilities.equity.total}
        shareCapital={effectiveBS.equityAndLiabilities.equity.shareCapital}
      />

      {/* Accounting Standard Selector Modal (K2/K3) */}
      {/* Triple-check: state + prop + location state to prevent modal after standard change via "(byt)" */}
      <AccountingStandardSelector
        isOpen={showAccountingStandardSelector && !isStandardConfirmed && !standardJustConfirmed}
        report={report}
        onUpdate={onUpdate}
        onClose={() => setShowAccountingStandardSelector(false)}
        onConfirmed={async (standard) => {
          setShowAccountingStandardSelector(false);
          // Save the confirmation to userInput so it persists
          const updatedInput = {
            ...userInputRef.current,
            accountingStandardConfirmed: true,
            accountingStandard: standard,
          };
          if (onSaveInput) {
            const result = await onSaveInput(updatedInput);
            // Update parent state if report returned
            if (result.success && result.report) {
              onUpdate(result.report);
            }
          } else {
            try {
              await reportsApi.updateInput(report.id, updatedInput);
              // Update parent state with new userInput so WizardLayout shows (byt) button
              onUpdate({
                ...report,
                userInput: updatedInput,
              });
            } catch (err) {
              console.error("Failed to save accounting standard confirmation:", err);
            }
          }
        }}
        onSaveInput={onSaveInput}
      />

      {/* Suggested Journal Entries */}
      {sieData.suggestedEntries && sieData.suggestedEntries.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-4">
            <ClipboardList className="text-purple-600" size={20} />
            <h3 className="font-semibold text-purple-800">
              Förslag på bokslutsverifikationer
            </h3>
          </div>
          <p className="text-sm text-purple-700 mb-4">
            Följande verifikationer kan behövas för att slutföra bokslutet. Utför dessa i ditt bokföringsprogram och ladda sedan upp en ny SIE-fil.
          </p>
          <div className="space-y-4">
            {sieData.suggestedEntries.map((entry, index) => (
              <div key={entry.id} className="bg-white rounded-lg border border-purple-100 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      {index + 1}. {entry.title}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        entry.confidence === "high"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {entry.confidence === "high" ? "Hög säkerhet" : "Medel säkerhet"}
                      </span>
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">{entry.description}</p>
                  </div>
                  <span className="text-lg font-bold text-purple-700">
                    {formatNumber(entry.totalAmount)} kr
                  </span>
                </div>

                <div className="mt-3 bg-gray-50 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Konto</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Namn</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Debet</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Kredit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.lines.map((line, lineIndex) => (
                        <tr key={lineIndex} className="border-t border-gray-200">
                          <td className="py-2 px-3 font-mono text-gray-800">{line.accountNumber}</td>
                          <td className="py-2 px-3 text-gray-600">{line.accountName}</td>
                          <td className="py-2 px-3 text-right font-medium">
                            {line.debit > 0 ? formatNumber(line.debit) : ""}
                          </td>
                          <td className="py-2 px-3 text-right font-medium">
                            {line.credit > 0 ? formatNumber(line.credit) : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="mt-3 text-xs text-gray-500 italic">
                  {entry.reason}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Figures */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-gray-600">Nettoomsättning</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatNumber(effectiveKeyFigures.netSales)} kr
          </p>
          {prevKF && (
            <p className="text-xs text-gray-500 mt-1">
              Fg år: {formatNumber(prevKF.netSales)} kr
            </p>
          )}
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Årets resultat</p>
          <p
            className={`text-2xl font-bold mt-1 ${
              effectiveKeyFigures.netResult >= 0
                ? "text-green-600"
                : "text-red-600"
            }`}
          >
            {formatNumber(effectiveKeyFigures.netResult)} kr
          </p>
          {prevKF && (
            <p className="text-xs text-gray-500 mt-1">
              Fg år: {formatNumber(prevKF.netResult)} kr
            </p>
          )}
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Soliditet</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatPercent(effectiveKeyFigures.solidityPercent)}
          </p>
          {prevKF && (
            <p className="text-xs text-gray-500 mt-1">
              Fg år: {formatPercent(prevKF.solidityPercent)}
            </p>
          )}
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Fritt eget kapital</p>
          <p
            className={`text-2xl font-bold mt-1 ${
              effectiveKeyFigures.freeEquity >= 0
                ? "text-gray-900"
                : "text-red-600"
            }`}
          >
            {formatNumber(effectiveKeyFigures.freeEquity)} kr
          </p>
          {prevKF && (
            <p className="text-xs text-gray-500 mt-1">
              Fg år: {formatNumber(prevKF.freeEquity)} kr
            </p>
          )}
        </div>
      </div>

      {/* Blockers shown right before financial statements */}
      {blockers.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <XCircle className="text-red-600" size={20} />
            <h3 className="font-semibold text-red-800">
              Fel som måste åtgärdas ({blockers.length})
            </h3>
          </div>
          <ul className="space-y-2">
            {blockers.map((issue, index) => (
              <li key={index} className="flex items-start space-x-2">
                <span className="text-red-600 mt-0.5">•</span>
                <div>
                  <p className="text-sm text-red-800">{issue.message}</p>
                  {issue.legalRef && (
                    <p className="text-xs text-red-600 mt-0.5">
                      Ref: {issue.legalRef}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* K2 2026 Eligibility - Confirmed status (questions are now in Step 1) */}
      {isFiscalYear2026OrLater && k2EligibilityConfirmed && (
        <div className={`rounded-lg p-4 ${k2EligibilityPasses ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-3">
            {k2EligibilityPasses ? (
              <>
                <ShieldCheck size={20} className="text-green-600" />
                <div>
                  <p className="font-medium text-green-800">K2-behörighet bekräftad</p>
                  <p className="text-sm text-green-600">Företaget uppfyller kraven för att tillämpa K2 för räkenskapsår 2026+</p>
                </div>
              </>
            ) : (
              <>
                <ShieldAlert size={20} className="text-red-600" />
                <div>
                  <p className="font-medium text-red-800">Företaget uppfyller INTE K2-kraven</p>
                  <p className="text-sm text-red-600">Företag med filialer i utlandet, kryptotillgångar eller aktierelaterade ersättningar får inte tillämpa K2 från 2026</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Auto-save indicator + status */}
      <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
        <div className="flex items-center space-x-4">
          {/* Save status */}
          <div className="flex items-center space-x-2">
            {saveError ? (
              <>
                <AlertTriangle size={14} className="text-amber-500" />
                <span className="text-sm text-amber-700">{saveError}</span>
              </>
            ) : hasUnsavedChanges ? (
              <>
                <Loader2 size={14} className="text-primary-500 animate-spin" />
                <span className="text-sm text-gray-600">Sparar...</span>
              </>
            ) : (
              <>
                <CheckCircle size={14} className="text-green-500" />
                <span className="text-sm text-gray-600">Sparat</span>
              </>
            )}
          </div>

          {/* Validation status */}
          {blockers.length === 0 ? (
            <div className="flex items-center space-x-1 text-green-600">
              <CheckCircle size={14} />
              <span className="text-sm font-medium">Balanserar</span>
            </div>
          ) : (
            <div className="flex items-center space-x-1 text-red-600">
              <XCircle size={14} />
              <span className="text-sm font-medium">{blockers.length} fel</span>
            </div>
          )}

          {overrideCount > 0 && (
            <span className="text-xs text-gray-400">
              ({overrideCount} fält ändrade)
            </span>
          )}
        </div>

        {overrideCount > 0 && (
          <button
            onClick={handleResetAll}
            className="text-sm text-gray-500 hover:text-primary-600 flex items-center"
          >
            <RotateCcw size={14} className="mr-1" />
            Återställ alla
          </button>
        )}
      </div>

      {/* Financial Statements */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Income Statement */}
        <div className="card">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-sm font-bold text-gray-900 flex items-center">
              <TrendingUp className="mr-2 text-primary-600" size={18} />
              Resultaträkning
            </h3>
            <button
              onClick={() => toggleAllSectionsInBlock(INCOME_STATEMENT_SECTIONS)}
              className="text-[10px] text-gray-500 hover:text-primary-600 flex items-center bg-gray-50 hover:bg-primary-50 px-2 py-1 rounded transition-colors"
            >
              {areAllSectionsExpanded(INCOME_STATEMENT_SECTIONS) ? (
                <>
                  <ChevronDown size={10} className="mr-1" />
                  Fäll ihop alla
                </>
              ) : (
                <>
                  <ChevronRight size={10} className="mr-1" />
                  Expandera alla
                </>
              )}
            </button>
          </div>

          {/* Column headers */}
          <div className="flex items-center justify-end text-[10px] font-medium text-gray-500 pb-2 border-b border-gray-200 mb-2">
            <div className="min-w-[90px] text-right leading-tight">
              <div>{currentYearPeriod.start}</div>
              <div>{currentYearPeriod.end}</div>
            </div>
            {hasPreviousYear && (
              <div className="min-w-[80px] text-right ml-3 leading-tight">
                <div>{previousYearPeriod.start}</div>
                <div>{previousYearPeriod.end}</div>
              </div>
            )}
          </div>

          <div className="space-y-1">
            {/* Revenue Section */}
            <ExpandableSection
              title="Rörelseintäkter"
              sum={effectiveIS.revenue.total}
              originalSum={originalIS.revenue.total}
              prevYearSum={prevIS?.revenue.total}
              isExpanded={expandedSections.has("revenue")}
              onToggle={() => toggleSection("revenue")}
            >
              <EditableRow
                label="Nettoomsättning"
                value={effectiveIS.revenue.netSales}
                originalValue={originalIS.revenue.netSales}
                prevYearValue={prevIS?.revenue.netSales}
                onChange={(v) =>
                  handleOverride("incomeStatement", "revenue.netSales", v)
                }
                onReset={() =>
                  handleReset("incomeStatement", "revenue.netSales")
                }
              />
              <EditableRow
                label="Övriga rörelseintäkter"
                value={effectiveIS.revenue.otherIncome}
                originalValue={originalIS.revenue.otherIncome}
                prevYearValue={prevIS?.revenue.otherIncome}
                onChange={(v) =>
                  handleOverride("incomeStatement", "revenue.otherIncome", v)
                }
                onReset={() =>
                  handleReset("incomeStatement", "revenue.otherIncome")
                }
              />
            </ExpandableSection>

            {/* Expenses Section */}
            <ExpandableSection
              title="Rörelsekostnader"
              sum={-effectiveIS.expenses.total}
              originalSum={-originalIS.expenses.total}
              prevYearSum={prevIS ? -prevIS.expenses.total : undefined}
              isExpanded={expandedSections.has("expenses")}
              onToggle={() => toggleSection("expenses")}
              isNegative
            >
              <EditableRow
                label="Råvaror och förnödenheter"
                value={effectiveIS.expenses.goodsAndMaterials}
                originalValue={originalIS.expenses.goodsAndMaterials}
                prevYearValue={prevIS?.expenses.goodsAndMaterials}
                onChange={(v) =>
                  handleOverride(
                    "incomeStatement",
                    "expenses.goodsAndMaterials",
                    v
                  )
                }
                onReset={() =>
                  handleReset("incomeStatement", "expenses.goodsAndMaterials")
                }
                isNegative
              />
              <EditableRow
                label="Övriga externa kostnader"
                value={effectiveIS.expenses.otherExternalCosts}
                originalValue={originalIS.expenses.otherExternalCosts}
                prevYearValue={prevIS?.expenses.otherExternalCosts}
                onChange={(v) =>
                  handleOverride(
                    "incomeStatement",
                    "expenses.otherExternalCosts",
                    v
                  )
                }
                onReset={() =>
                  handleReset("incomeStatement", "expenses.otherExternalCosts")
                }
                isNegative
              />
              <EditableRow
                label="Personalkostnader"
                value={effectiveIS.expenses.personnelCosts}
                originalValue={originalIS.expenses.personnelCosts}
                prevYearValue={prevIS?.expenses.personnelCosts}
                onChange={(v) =>
                  handleOverride(
                    "incomeStatement",
                    "expenses.personnelCosts",
                    v
                  )
                }
                onReset={() =>
                  handleReset("incomeStatement", "expenses.personnelCosts")
                }
                isNegative
              />
              <EditableRow
                label="Avskrivningar"
                value={effectiveIS.expenses.depreciationAmortization}
                originalValue={originalIS.expenses.depreciationAmortization}
                prevYearValue={prevIS?.expenses.depreciationAmortization}
                onChange={(v) =>
                  handleOverride(
                    "incomeStatement",
                    "expenses.depreciationAmortization",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "incomeStatement",
                    "expenses.depreciationAmortization"
                  )
                }
                isNegative
              />
              <EditableRow
                label="Övriga rörelsekostnader"
                value={effectiveIS.expenses.otherOperatingExpenses}
                originalValue={originalIS.expenses.otherOperatingExpenses}
                prevYearValue={prevIS?.expenses.otherOperatingExpenses}
                onChange={(v) =>
                  handleOverride(
                    "incomeStatement",
                    "expenses.otherOperatingExpenses",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "incomeStatement",
                    "expenses.otherOperatingExpenses"
                  )
                }
                isNegative
              />
            </ExpandableSection>

            {/* Operating Result */}
            <SummaryRow
              label="Rörelseresultat"
              value={effectiveIS.operatingResult}
              originalValue={originalIS.operatingResult}
              prevYearValue={prevIS?.operatingResult}
            />

            {/* Financial Items Section */}
            <ExpandableSection
              title="Finansiella poster"
              sum={effectiveIS.financialItems.total}
              originalSum={originalIS.financialItems.total}
              prevYearSum={prevIS?.financialItems.total}
              isExpanded={expandedSections.has("financial")}
              onToggle={() => toggleSection("financial")}
            >
              <EditableRow
                label="Ränteintäkter m.m."
                value={effectiveIS.financialItems.financialIncome}
                originalValue={originalIS.financialItems.financialIncome}
                prevYearValue={prevIS?.financialItems.financialIncome}
                onChange={(v) =>
                  handleOverride(
                    "incomeStatement",
                    "financialItems.financialIncome",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "incomeStatement",
                    "financialItems.financialIncome"
                  )
                }
              />
              <EditableRow
                label="Räntekostnader m.m."
                value={effectiveIS.financialItems.financialExpenses}
                originalValue={originalIS.financialItems.financialExpenses}
                prevYearValue={prevIS?.financialItems.financialExpenses}
                onChange={(v) =>
                  handleOverride(
                    "incomeStatement",
                    "financialItems.financialExpenses",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "incomeStatement",
                    "financialItems.financialExpenses"
                  )
                }
                isNegative
              />
            </ExpandableSection>

            {/* Other items */}
            <ExpandableSection
              title="Bokslutsdispositioner m.m."
              sum={
                effectiveIS.extraordinaryItems +
                effectiveIS.appropriations -
                effectiveIS.taxOnResult
              }
              originalSum={
                originalIS.extraordinaryItems +
                originalIS.appropriations -
                originalIS.taxOnResult
              }
              prevYearSum={prevIS ? (
                prevIS.extraordinaryItems +
                prevIS.appropriations -
                prevIS.taxOnResult
              ) : undefined}
              isExpanded={expandedSections.has("other")}
              onToggle={() => toggleSection("other")}
            >
              <EditableRow
                label="Extraordinära poster"
                value={effectiveIS.extraordinaryItems}
                originalValue={originalIS.extraordinaryItems}
                prevYearValue={prevIS?.extraordinaryItems}
                onChange={(v) =>
                  handleOverride("incomeStatement", "extraordinaryItems", v)
                }
                onReset={() =>
                  handleReset("incomeStatement", "extraordinaryItems")
                }
              />
              <EditableRow
                label="Bokslutsdispositioner"
                value={effectiveIS.appropriations}
                originalValue={originalIS.appropriations}
                prevYearValue={prevIS?.appropriations}
                onChange={(v) =>
                  handleOverride("incomeStatement", "appropriations", v)
                }
                onReset={() =>
                  handleReset("incomeStatement", "appropriations")
                }
              />
              <EditableRow
                label="Skatt på årets resultat"
                value={effectiveIS.taxOnResult}
                originalValue={originalIS.taxOnResult}
                prevYearValue={prevIS?.taxOnResult}
                onChange={(v) =>
                  handleOverride("incomeStatement", "taxOnResult", v)
                }
                onReset={() => handleReset("incomeStatement", "taxOnResult")}
                isNegative
              />
            </ExpandableSection>

            {/* Net Result */}
            <div className="bg-primary-50 rounded-lg px-2 py-1.5 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-primary-900">Årets resultat</span>
                <div className="flex items-center gap-3">
                  <ReadOnlyNumberField
                    value={effectiveIS.netResult}
                    originalValue={originalIS.netResult}
                    bold
                    highlight
                    small
                  />
                  {prevIS && (
                    <span className="text-xs font-bold text-primary-400 min-w-[70px] text-right tabular-nums">
                      {formatNumber(prevIS.netResult)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Balance Sheet */}
        <div className="card">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-sm font-bold text-gray-900 flex items-center">
              <TrendingDown className="mr-2 text-primary-600" size={18} />
              Balansräkning
            </h3>
            <button
              onClick={() => toggleAllSectionsInBlock(BALANCE_SHEET_SECTIONS)}
              className="text-[10px] text-gray-500 hover:text-primary-600 flex items-center bg-gray-50 hover:bg-primary-50 px-2 py-1 rounded transition-colors"
            >
              {areAllSectionsExpanded(BALANCE_SHEET_SECTIONS) ? (
                <>
                  <ChevronDown size={10} className="mr-1" />
                  Fäll ihop alla
                </>
              ) : (
                <>
                  <ChevronRight size={10} className="mr-1" />
                  Expandera alla
                </>
              )}
            </button>
          </div>

          {/* Column headers */}
          <div className="flex items-center justify-end text-[10px] font-medium text-gray-500 pb-2 border-b border-gray-200 mb-2">
            <div className="min-w-[90px] text-right leading-tight">
              <div>{currentYearPeriod.start}</div>
              <div>{currentYearPeriod.end}</div>
            </div>
            {hasPreviousYear && (
              <div className="min-w-[80px] text-right ml-3 leading-tight">
                <div>{previousYearPeriod.start}</div>
                <div>{previousYearPeriod.end}</div>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider py-1">
              Tillgångar
            </div>

            {/* Fixed Assets */}
            <ExpandableSection
              title="Anläggningstillgångar"
              sum={effectiveBS.assets.fixedAssets.total}
              originalSum={originalBS.assets.fixedAssets.total}
              prevYearSum={prevBS?.assets.fixedAssets.total}
              isExpanded={expandedSections.has("fixedAssets")}
              onToggle={() => toggleSection("fixedAssets")}
            >
              <EditableRow
                label="Immateriella tillgångar"
                value={effectiveBS.assets.fixedAssets.intangible}
                originalValue={originalBS.assets.fixedAssets.intangible}
                prevYearValue={prevBS?.assets.fixedAssets.intangible}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "assets.fixedAssets.intangible",
                    v
                  )
                }
                onReset={() =>
                  handleReset("balanceSheet", "assets.fixedAssets.intangible")
                }
              />
              {/* In manual entry mode, show separate fields for buildings and machinery */}
              {isManualEntryMode ? (
                <>
                  <EditableRow
                    label="Byggnader och mark"
                    value={effectiveBS.assets.fixedAssets.buildings}
                    originalValue={originalBS.assets.fixedAssets.buildings ?? 0}
                    prevYearValue={prevBS?.assets.fixedAssets.buildings}
                    onChange={(v) =>
                      handleOverride(
                        "balanceSheet",
                        "assets.fixedAssets.buildings",
                        v
                      )
                    }
                    onReset={() =>
                      handleReset("balanceSheet", "assets.fixedAssets.buildings")
                    }
                  />
                  <EditableRow
                    label="Maskiner och inventarier"
                    value={effectiveBS.assets.fixedAssets.machinery}
                    originalValue={originalBS.assets.fixedAssets.machinery ?? 0}
                    prevYearValue={prevBS?.assets.fixedAssets.machinery}
                    onChange={(v) =>
                      handleOverride(
                        "balanceSheet",
                        "assets.fixedAssets.machinery",
                        v
                      )
                    }
                    onReset={() =>
                      handleReset("balanceSheet", "assets.fixedAssets.machinery")
                    }
                  />
                </>
              ) : (
                <EditableRow
                  label="Materiella tillgångar"
                  value={effectiveBS.assets.fixedAssets.tangible}
                  originalValue={originalBS.assets.fixedAssets.tangible}
                  prevYearValue={prevBS?.assets.fixedAssets.tangible}
                  onChange={(v) =>
                    handleOverride(
                      "balanceSheet",
                      "assets.fixedAssets.tangible",
                      v
                    )
                  }
                  onReset={() =>
                    handleReset("balanceSheet", "assets.fixedAssets.tangible")
                  }
                />
              )}
              <EditableRow
                label="Finansiella tillgångar"
                value={effectiveBS.assets.fixedAssets.financial}
                originalValue={originalBS.assets.fixedAssets.financial}
                prevYearValue={prevBS?.assets.fixedAssets.financial}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "assets.fixedAssets.financial",
                    v
                  )
                }
                onReset={() =>
                  handleReset("balanceSheet", "assets.fixedAssets.financial")
                }
              />
            </ExpandableSection>

            {/* Current Assets */}
            <ExpandableSection
              title="Omsättningstillgångar"
              sum={effectiveBS.assets.currentAssets.total}
              originalSum={originalBS.assets.currentAssets.total}
              prevYearSum={prevBS?.assets.currentAssets.total}
              isExpanded={expandedSections.has("currentAssets")}
              onToggle={() => toggleSection("currentAssets")}
            >
              <EditableRow
                label="Varulager"
                value={effectiveBS.assets.currentAssets.inventory}
                originalValue={originalBS.assets.currentAssets.inventory}
                prevYearValue={prevBS?.assets.currentAssets.inventory}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "assets.currentAssets.inventory",
                    v
                  )
                }
                onReset={() =>
                  handleReset("balanceSheet", "assets.currentAssets.inventory")
                }
              />
              <EditableRow
                label="Kundfordringar"
                value={effectiveBS.assets.currentAssets.receivables}
                originalValue={originalBS.assets.currentAssets.receivables}
                prevYearValue={prevBS?.assets.currentAssets.receivables}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "assets.currentAssets.receivables",
                    v
                  )
                }
                onReset={() =>
                  handleReset("balanceSheet", "assets.currentAssets.receivables")
                }
              />
              <EditableRow
                label="Kortfristiga placeringar"
                value={effectiveBS.assets.currentAssets.shortTermInvestments}
                originalValue={
                  originalBS.assets.currentAssets.shortTermInvestments
                }
                prevYearValue={prevBS?.assets.currentAssets.shortTermInvestments}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "assets.currentAssets.shortTermInvestments",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "balanceSheet",
                    "assets.currentAssets.shortTermInvestments"
                  )
                }
              />
              <EditableRow
                label="Kassa och bank"
                value={effectiveBS.assets.currentAssets.cashAndBank}
                originalValue={originalBS.assets.currentAssets.cashAndBank}
                prevYearValue={prevBS?.assets.currentAssets.cashAndBank}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "assets.currentAssets.cashAndBank",
                    v
                  )
                }
                onReset={() =>
                  handleReset("balanceSheet", "assets.currentAssets.cashAndBank")
                }
              />
            </ExpandableSection>

            <SummaryRow
              label="Summa tillgångar"
              value={effectiveBS.assets.totalAssets}
              originalValue={originalBS.assets.totalAssets}
              prevYearValue={prevBS?.assets.totalAssets}
            />

            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider py-1 mt-4">
              Eget kapital & skulder
            </div>

            {/* Equity */}
            <ExpandableSection
              title="Eget kapital"
              sum={effectiveBS.equityAndLiabilities.equity.total}
              originalSum={originalBS.equityAndLiabilities.equity.total}
              prevYearSum={prevBS?.equityAndLiabilities.equity.total}
              isExpanded={expandedSections.has("equity")}
              onToggle={() => toggleSection("equity")}
            >
              <EditableRow
                label="Aktiekapital"
                value={effectiveBS.equityAndLiabilities.equity.shareCapital}
                originalValue={
                  originalBS.equityAndLiabilities.equity.shareCapital
                }
                prevYearValue={prevBS?.equityAndLiabilities.equity.shareCapital}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "equityAndLiabilities.equity.shareCapital",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "balanceSheet",
                    "equityAndLiabilities.equity.shareCapital"
                  )
                }
              />
              {/* Ej registrerat aktiekapital - visa endast om > 0 */}
              {effectiveBS.equityAndLiabilities.equity.unregisteredShareCapital > 0 && (
                <EditableRow
                  label="Ej registrerat aktiekapital"
                  value={effectiveBS.equityAndLiabilities.equity.unregisteredShareCapital}
                  originalValue={
                    originalBS.equityAndLiabilities.equity.unregisteredShareCapital ?? 0
                  }
                  prevYearValue={prevBS?.equityAndLiabilities.equity.unregisteredShareCapital}
                  onChange={(v) =>
                    handleOverride(
                      "balanceSheet",
                      "equityAndLiabilities.equity.unregisteredShareCapital",
                      v
                    )
                  }
                  onReset={() =>
                    handleReset(
                      "balanceSheet",
                      "equityAndLiabilities.equity.unregisteredShareCapital"
                    )
                  }
                />
              )}
              {/* Uppskrivningsfond - visa endast om > 0 */}
              {effectiveBS.equityAndLiabilities.equity.revaluationReserve > 0 && (
                <EditableRow
                  label="Uppskrivningsfond"
                  value={effectiveBS.equityAndLiabilities.equity.revaluationReserve}
                  originalValue={
                    originalBS.equityAndLiabilities.equity.revaluationReserve
                  }
                  prevYearValue={prevBS?.equityAndLiabilities.equity.revaluationReserve}
                  onChange={(v) =>
                    handleOverride(
                      "balanceSheet",
                      "equityAndLiabilities.equity.revaluationReserve",
                      v
                    )
                  }
                  onReset={() =>
                    handleReset(
                      "balanceSheet",
                      "equityAndLiabilities.equity.revaluationReserve"
                    )
                  }
                />
              )}
              <EditableRow
                label="Reservfond"
                value={effectiveBS.equityAndLiabilities.equity.legalReserve}
                originalValue={
                  originalBS.equityAndLiabilities.equity.legalReserve
                }
                prevYearValue={prevBS?.equityAndLiabilities.equity.legalReserve}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "equityAndLiabilities.equity.legalReserve",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "balanceSheet",
                    "equityAndLiabilities.equity.legalReserve"
                  )
                }
              />
              {/* Fond för utvecklingsutgifter - visa endast om > 0 */}
              {effectiveBS.equityAndLiabilities.equity.developmentFund > 0 && (
                <EditableRow
                  label="Fond för utvecklingsutgifter"
                  value={effectiveBS.equityAndLiabilities.equity.developmentFund}
                  originalValue={
                    originalBS.equityAndLiabilities.equity.developmentFund ?? 0
                  }
                  prevYearValue={prevBS?.equityAndLiabilities.equity.developmentFund}
                  onChange={(v) =>
                    handleOverride(
                      "balanceSheet",
                      "equityAndLiabilities.equity.developmentFund",
                      v
                    )
                  }
                  onReset={() =>
                    handleReset(
                      "balanceSheet",
                      "equityAndLiabilities.equity.developmentFund"
                    )
                  }
                />
              )}
              {/* Överkursfond - visa endast om > 0 */}
              {effectiveBS.equityAndLiabilities.equity.sharePremium > 0 && (
                <EditableRow
                  label="Överkursfond"
                  value={effectiveBS.equityAndLiabilities.equity.sharePremium}
                  originalValue={
                    originalBS.equityAndLiabilities.equity.sharePremium ?? 0
                  }
                  prevYearValue={prevBS?.equityAndLiabilities.equity.sharePremium}
                  onChange={(v) =>
                    handleOverride(
                      "balanceSheet",
                      "equityAndLiabilities.equity.sharePremium",
                      v
                    )
                  }
                  onReset={() =>
                    handleReset(
                      "balanceSheet",
                      "equityAndLiabilities.equity.sharePremium"
                    )
                  }
                />
              )}
              <EditableRow
                label="Balanserat resultat"
                value={effectiveBS.equityAndLiabilities.equity.retainedEarnings}
                originalValue={
                  originalBS.equityAndLiabilities.equity.retainedEarnings
                }
                prevYearValue={prevBS?.equityAndLiabilities.equity.retainedEarnings}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "equityAndLiabilities.equity.retainedEarnings",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "balanceSheet",
                    "equityAndLiabilities.equity.retainedEarnings"
                  )
                }
              />
              {/* Årets resultat (read-only, calculated from income statement) */}
              <div className="flex items-center justify-between py-1 pl-3 text-xs">
                <span className="text-gray-500 italic">Årets resultat</span>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-400 tabular-nums">
                    {formatNumber(effectiveIS.netResult)}
                  </span>
                  {prevBS && (
                    <span className="text-xs text-gray-400 min-w-[70px] text-right tabular-nums">
                      {formatNumber(prevBS.equityAndLiabilities.equity.currentYearResult)}
                    </span>
                  )}
                </div>
              </div>

              {/* Förändringar i eget kapital - collapsible subsection */}
              <div className="mt-4 pt-3 border-t-2 border-primary-200 bg-primary-50/50 -mx-2 px-2 pb-2 rounded-b-lg">
                <button
                  onClick={() => setShowEquityChangesSection(!showEquityChangesSection)}
                  className="w-full flex items-center justify-between text-sm text-primary-700 hover:text-primary-800 transition-colors py-1"
                >
                  <span className="flex items-center gap-2 font-medium">
                    <BarChart2 size={16} className="text-primary-500" />
                    Årets förändringar i eget kapital
                    {(equityChanges.dividendPaidDuringYear || equityChanges.shareIssue || equityChanges.shareholderContributions?.length) && (
                      <span className="text-xs bg-primary-100 text-primary-600 px-1.5 py-0.5 rounded">(har ändringar)</span>
                    )}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-primary-500">
                    {showEquityChangesSection ? 'Dölj' : 'Visa'}
                    {showEquityChangesSection ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                </button>

                {showEquityChangesSection && (
                  <div className="mt-3 space-y-3 pl-2 border-l-2 border-primary-100">
                    {/* Utdelning under året */}
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-600">Utbetald utdelning under året</label>
                      <input
                        type="number"
                        value={equityChanges.dividendPaidDuringYear || ''}
                        onChange={(e) => setEquityChanges({
                          ...equityChanges,
                          dividendPaidDuringYear: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        placeholder="0"
                        className="w-24 px-2 py-1 text-xs text-right border border-gray-200 rounded focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                      />
                    </div>

                    {/* Nyemission */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="hasShareIssue"
                          checked={!!equityChanges.shareIssue}
                          onChange={(e) => setEquityChanges({
                            ...equityChanges,
                            shareIssue: e.target.checked ? { totalShareCapitalIncrease: 0, type: 'cash' } : undefined
                          })}
                          className="w-3 h-3 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <label htmlFor="hasShareIssue" className="text-xs text-gray-600">Nyemission under året</label>
                      </div>
                      {equityChanges.shareIssue && (
                        <div className="pl-5 space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-xs text-gray-500">Ökning aktiekapital</label>
                            <input
                              type="number"
                              value={equityChanges.shareIssue.totalShareCapitalIncrease || ''}
                              onChange={(e) => setEquityChanges({
                                ...equityChanges,
                                shareIssue: {
                                  ...equityChanges.shareIssue!,
                                  totalShareCapitalIncrease: e.target.value ? parseFloat(e.target.value) : 0
                                }
                              })}
                              placeholder="0"
                              className="w-24 px-2 py-1 text-xs text-right border border-gray-200 rounded focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <label className="text-xs text-gray-500">Överkurs (överkursfond)</label>
                            <input
                              type="number"
                              value={equityChanges.shareIssue.sharePremiumAmount || ''}
                              onChange={(e) => setEquityChanges({
                                ...equityChanges,
                                shareIssue: {
                                  ...equityChanges.shareIssue!,
                                  sharePremiumAmount: e.target.value ? parseFloat(e.target.value) : undefined
                                }
                              })}
                              placeholder="0"
                              className="w-24 px-2 py-1 text-xs text-right border border-gray-200 rounded focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Aktieägartillskott */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="hasShareholderContributions"
                          checked={!!(equityChanges.shareholderContributions && equityChanges.shareholderContributions.length > 0)}
                          onChange={(e) => setEquityChanges({
                            ...equityChanges,
                            shareholderContributions: e.target.checked ? [{ amount: 0, type: 'unconditional' }] : undefined
                          })}
                          className="w-3 h-3 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <label htmlFor="hasShareholderContributions" className="text-xs text-gray-600">Aktieägartillskott under året</label>
                      </div>
                      {equityChanges.shareholderContributions && equityChanges.shareholderContributions.length > 0 && (
                        <div className="pl-5 space-y-2">
                          {equityChanges.shareholderContributions.map((contrib, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <select
                                value={contrib.type}
                                onChange={(e) => {
                                  const updated = [...equityChanges.shareholderContributions!];
                                  updated[idx] = { ...updated[idx], type: e.target.value as 'conditional' | 'unconditional' };
                                  setEquityChanges({ ...equityChanges, shareholderContributions: updated });
                                }}
                                className="px-2 py-1 text-xs border border-gray-200 rounded"
                              >
                                <option value="unconditional">Ovillkorat</option>
                                <option value="conditional">Villkorat</option>
                              </select>
                              <input
                                type="number"
                                value={contrib.amount || ''}
                                onChange={(e) => {
                                  const updated = [...equityChanges.shareholderContributions!];
                                  updated[idx] = { ...updated[idx], amount: e.target.value ? parseFloat(e.target.value) : 0 };
                                  setEquityChanges({ ...equityChanges, shareholderContributions: updated });
                                }}
                                placeholder="Belopp"
                                className="w-24 px-2 py-1 text-xs text-right border border-gray-200 rounded focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                              />
                              {equityChanges.shareholderContributions!.length > 1 && (
                                <button
                                  onClick={() => {
                                    const updated = equityChanges.shareholderContributions!.filter((_, i) => i !== idx);
                                    setEquityChanges({ ...equityChanges, shareholderContributions: updated });
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => setEquityChanges({
                              ...equityChanges,
                              shareholderContributions: [
                                ...(equityChanges.shareholderContributions || []),
                                { amount: 0, type: 'unconditional' }
                              ]
                            })}
                            className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                          >
                            <Plus size={12} /> Lägg till
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Koncernbidrag */}
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-600">Koncernbidrag erhållet</label>
                      <input
                        type="number"
                        value={equityChanges.groupContributionReceived || ''}
                        onChange={(e) => setEquityChanges({
                          ...equityChanges,
                          groupContributionReceived: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        placeholder="0"
                        className="w-24 px-2 py-1 text-xs text-right border border-gray-200 rounded focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-600">Koncernbidrag lämnat</label>
                      <input
                        type="number"
                        value={equityChanges.groupContributionGiven || ''}
                        onChange={(e) => setEquityChanges({
                          ...equityChanges,
                          groupContributionGiven: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        placeholder="0"
                        className="w-24 px-2 py-1 text-xs text-right border border-gray-200 rounded focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                      />
                    </div>

                    <p className="text-[10px] text-gray-400 italic mt-2">
                      Dessa uppgifter används i förvaltningsberättelsens förändring av eget kapital (K2 5.7)
                    </p>
                  </div>
                )}
              </div>
            </ExpandableSection>

            {/* Liabilities */}
            <ExpandableSection
              title="Skulder & avsättningar"
              sum={
                effectiveBS.equityAndLiabilities.untaxedReserves +
                effectiveBS.equityAndLiabilities.provisions +
                effectiveBS.equityAndLiabilities.longTermLiabilities +
                effectiveBS.equityAndLiabilities.shortTermLiabilities
              }
              originalSum={
                originalBS.equityAndLiabilities.untaxedReserves +
                originalBS.equityAndLiabilities.provisions +
                originalBS.equityAndLiabilities.longTermLiabilities +
                originalBS.equityAndLiabilities.shortTermLiabilities
              }
              prevYearSum={prevBS ? (
                prevBS.equityAndLiabilities.untaxedReserves +
                prevBS.equityAndLiabilities.provisions +
                prevBS.equityAndLiabilities.longTermLiabilities +
                prevBS.equityAndLiabilities.shortTermLiabilities
              ) : undefined}
              isExpanded={expandedSections.has("liabilities")}
              onToggle={() => toggleSection("liabilities")}
            >
              <EditableRow
                label="Obeskattade reserver"
                value={effectiveBS.equityAndLiabilities.untaxedReserves}
                originalValue={originalBS.equityAndLiabilities.untaxedReserves}
                prevYearValue={prevBS?.equityAndLiabilities.untaxedReserves}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "equityAndLiabilities.untaxedReserves",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "balanceSheet",
                    "equityAndLiabilities.untaxedReserves"
                  )
                }
              />
              <EditableRow
                label="Avsättningar"
                value={effectiveBS.equityAndLiabilities.provisions}
                originalValue={originalBS.equityAndLiabilities.provisions}
                prevYearValue={prevBS?.equityAndLiabilities.provisions}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "equityAndLiabilities.provisions",
                    v
                  )
                }
                onReset={() =>
                  handleReset("balanceSheet", "equityAndLiabilities.provisions")
                }
              />
              <EditableRow
                label="Långfristiga skulder"
                value={effectiveBS.equityAndLiabilities.longTermLiabilities}
                originalValue={
                  originalBS.equityAndLiabilities.longTermLiabilities
                }
                prevYearValue={prevBS?.equityAndLiabilities.longTermLiabilities}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "equityAndLiabilities.longTermLiabilities",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "balanceSheet",
                    "equityAndLiabilities.longTermLiabilities"
                  )
                }
              />
              <EditableRow
                label="Kortfristiga skulder"
                value={effectiveBS.equityAndLiabilities.shortTermLiabilities}
                originalValue={
                  originalBS.equityAndLiabilities.shortTermLiabilities
                }
                prevYearValue={prevBS?.equityAndLiabilities.shortTermLiabilities}
                onChange={(v) =>
                  handleOverride(
                    "balanceSheet",
                    "equityAndLiabilities.shortTermLiabilities",
                    v
                  )
                }
                onReset={() =>
                  handleReset(
                    "balanceSheet",
                    "equityAndLiabilities.shortTermLiabilities"
                  )
                }
              />
            </ExpandableSection>

            <SummaryRow
              label="Summa EK & skulder"
              value={effectiveBS.equityAndLiabilities.totalEquityAndLiabilities}
              originalValue={
                originalBS.equityAndLiabilities.totalEquityAndLiabilities
              }
              prevYearValue={prevBS?.equityAndLiabilities.totalEquityAndLiabilities}
            />
          </div>
        </div>
      </div>

      {/* Flerårsöversikt */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <BarChart2 className="mr-2 text-purple-600" size={20} />
            <div>
              <h3 className="text-sm font-bold text-gray-900">Flerårsöversikt</h3>
              <p className="text-xs text-gray-500">Belopp i kronor, visas som tkr i årsredovisningen</p>
            </div>
          </div>
        </div>

        {previousYears.length === 0 ? (
          <div className="text-center py-8">
            <BarChart2 className="mx-auto text-gray-300 mb-3" size={32} />
            {sieData ? (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  Flerårsöversikt saknas. Ladda in från SIE-filen eller lägg till manuellt.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => {
                      // Reload from SIE data
                      const currentYear = sieData.metadata.fiscalYearEnd.split("-")[0];
                      const entries: PreviousYearEntry[] = [{
                        year: currentYear,
                        netSales: effectiveKeyFigures.netSales,
                        resultAfterFinancialItems: effectiveIS.resultAfterFinancialItems,
                        solidityPercent: Math.round(effectiveKeyFigures.solidityPercent * 10) / 10,
                      }];
                      if (prevKF && prevIS) {
                        entries.push({
                          year: (parseInt(currentYear) - 1).toString(),
                          netSales: prevKF.netSales,
                          resultAfterFinancialItems: prevIS.resultAfterFinancialItems,
                          solidityPercent: Math.round(prevKF.solidityPercent * 10) / 10,
                        });
                      }
                      setPreviousYears(entries);
                    }}
                    className="btn btn-sm bg-purple-600 text-white hover:bg-purple-700 flex items-center"
                  >
                    <RefreshCw size={14} className="mr-1.5" />
                    Ladda in från SIE
                  </button>
                  <button
                    onClick={addManualYear}
                    className="btn btn-sm btn-secondary flex items-center"
                  >
                    <Plus size={14} className="mr-1.5" />
                    Lägg till manuellt
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  Ingen SIE-data tillgänglig. Lägg till manuellt för flerårsöversikt.
                </p>
                <button
                  onClick={addManualYear}
                  className="btn btn-sm btn-secondary flex items-center"
                >
                  <Plus size={14} className="mr-1.5" />
                  Lägg till manuellt
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 pr-4 text-xs font-medium text-gray-500">Nyckeltal</th>
                    {previousYears
                      .sort((a, b) => parseInt(b.year) - parseInt(a.year))
                      .map(entry => (
                        <th key={entry.year} className="text-right py-2 px-2 text-xs font-medium text-gray-900 min-w-[100px]">
                          {entry.year}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-xs text-gray-600">Nettoomsättning (kr)</td>
                    {previousYears
                      .sort((a, b) => parseInt(b.year) - parseInt(a.year))
                      .map(entry => (
                        <td key={entry.year} className="text-right py-2 px-2">
                          <input
                            type="text"
                            value={formatNumber(entry.netSales)}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value.replace(/\s/g, "").replace(",", ".")) || 0;
                              updateYearData(entry.year, "netSales", value);
                            }}
                            className="w-full text-right text-xs tabular-nums px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </td>
                      ))}
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-xs text-gray-600">Resultat efter fin. poster (kr)</td>
                    {previousYears
                      .sort((a, b) => parseInt(b.year) - parseInt(a.year))
                      .map(entry => (
                        <td key={entry.year} className="text-right py-2 px-2">
                          <input
                            type="text"
                            value={formatNumber(entry.resultAfterFinancialItems)}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value.replace(/\s/g, "").replace(",", ".")) || 0;
                              updateYearData(entry.year, "resultAfterFinancialItems", value);
                            }}
                            className={`w-full text-right text-xs tabular-nums px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                              entry.resultAfterFinancialItems >= 0 ? "text-green-600" : "text-red-600"
                            }`}
                          />
                        </td>
                      ))}
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-xs text-gray-600">Soliditet (%)</td>
                    {previousYears
                      .sort((a, b) => parseInt(b.year) - parseInt(a.year))
                      .map(entry => (
                        <td key={entry.year} className="text-right py-2 px-2">
                          <input
                            type="text"
                            value={entry.solidityPercent.toLocaleString("sv-SE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value.replace(",", ".")) || 0;
                              updateYearData(entry.year, "solidityPercent", value);
                            }}
                            className="w-full text-right text-xs tabular-nums px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </td>
                      ))}
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      <button
                        onClick={fillFromHistory}
                        className="text-xs text-purple-600 hover:text-purple-700 flex items-center"
                      >
                        <RefreshCw size={12} className="mr-1" />
                        Uppdatera
                      </button>
                    </td>
                    {previousYears
                      .sort((a, b) => parseInt(b.year) - parseInt(a.year))
                      .map(entry => (
                        <td key={entry.year} className="text-right py-2 px-2">
                          <button
                            onClick={() => removeYear(entry.year)}
                            className="text-gray-400 hover:text-red-500 p-1"
                            title="Ta bort år"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={addManualYear}
                className="text-xs text-primary-600 hover:text-primary-700 flex items-center"
              >
                <Plus size={14} className="mr-1" />
                Lägg till år
              </button>
            </div>
          </>
        )}
      </div>

      {/* Save error near continue button */}
      {saveError && !hasBlockers && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <div className="flex items-center space-x-2">
            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800">{saveError}</p>
          </div>
        </div>
      )}

      {/* Capital deficiency acknowledgement checkbox */}
      {warnings.some(w => w.code === "EQUITY_BELOW_HALF_SHARE_CAPITAL") && !hasBlockers && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={capitalDeficiencyAcknowledged}
              onChange={(e) => setCapitalDeficiencyAcknowledged(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
            />
            <div className="flex-1">
              <span className="font-medium text-amber-900 block">
                Jag har tagit del av informationen om kapitalbrist
              </span>
              <span className="text-sm text-amber-700 mt-1 block">
                Jag förstår att styrelsen enligt ABL 25 kap 13§ ska upprätta kontrollbalansräkning
                och att underlåtenhet kan medföra personligt betalningsansvar för styrelseledamöterna.
              </span>
            </div>
          </label>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          onClick={() => {
            window.scrollTo(0, 0);
            onNavigateStep ? onNavigateStep(prevStep) : navigate(`/dashboard/wizard/${report.id}/step/${prevStep}`);
          }}
          className="btn btn-secondary flex items-center"
        >
          <ArrowLeft size={20} className="mr-2" />
          Tillbaka
        </button>

        <button
          onClick={handleContinue}
          disabled={
            hasBlockers ||
            isSaving ||
            (warnings.some(w => w.code === "EQUITY_BELOW_HALF_SHARE_CAPITAL") && !capitalDeficiencyAcknowledged)
          }
          className="btn btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <Loader2 size={20} className="mr-2 animate-spin" />
              Sparar...
            </>
          ) : hasBlockers ? (
            "Åtgärda fel först"
          ) : warnings.some(w => w.code === "EQUITY_BELOW_HALF_SHARE_CAPITAL") && !capitalDeficiencyAcknowledged ? (
            "Bekräfta kapitalbrist först"
          ) : (
            <>
              Fortsätt
              <ArrowRight size={20} className="ml-2" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Expandable section component
interface ExpandableSectionProps {
  title: string;
  sum: number;
  originalSum: number;
  prevYearSum?: number;
  isExpanded: boolean;
  onToggle: () => void;
  isNegative?: boolean;
  children: React.ReactNode;
}

function ExpandableSection({
  title,
  sum,
  originalSum,
  prevYearSum,
  isExpanded,
  onToggle,
  children,
}: ExpandableSectionProps) {

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-2 py-1.5 bg-gray-50 hover:bg-gray-100 transition-colors group"
      >
        <div className="flex items-center gap-2">
          <div className={`flex items-center justify-center w-5 h-5 rounded transition-colors ${
            isExpanded
              ? "bg-primary-100 text-primary-600"
              : "bg-gray-200 text-gray-500 group-hover:bg-primary-50 group-hover:text-primary-500"
          }`}>
            {isExpanded ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
          </div>
          <span className="text-xs font-medium text-gray-700">{title}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
            isExpanded
              ? "bg-primary-100 text-primary-600"
              : "bg-gray-200 text-gray-500 group-hover:bg-primary-50 group-hover:text-primary-500"
          }`}>
            {isExpanded ? "Dölj" : "Redigera"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <ReadOnlyNumberField value={sum} originalValue={originalSum} small />
          {prevYearSum !== undefined && (
            <span className="text-xs text-gray-400 min-w-[70px] text-right tabular-nums">
              {formatNumber(prevYearSum)}
            </span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-2 py-1.5 space-y-0.5 bg-white border-t border-gray-100">{children}</div>
      )}
    </div>
  );
}

// Editable row component
interface EditableRowProps {
  label: string;
  value: number;
  originalValue: number;
  prevYearValue?: number;
  onChange: (value: number) => void;
  onReset: () => void;
  isNegative?: boolean;
}

function EditableRow({
  label,
  value,
  originalValue,
  prevYearValue,
  onChange,
  onReset,
  isNegative = false,
}: EditableRowProps) {
  return (
    <div className="flex items-center justify-between py-1 pl-3 text-xs">
      <span className="text-gray-600">{label}</span>
      <div className="flex items-center gap-3">
        <EditableNumberField
          value={value}
          originalValue={originalValue}
          onChange={onChange}
          onReset={onReset}
          isNegative={isNegative}
          small
        />
        {prevYearValue !== undefined && (
          <span className="text-xs text-gray-400 min-w-[70px] text-right tabular-nums">
            {formatNumber(isNegative ? -prevYearValue : prevYearValue)}
          </span>
        )}
      </div>
    </div>
  );
}

// Summary row (non-editable)
interface SummaryRowProps {
  label: string;
  value: number;
  originalValue: number;
  prevYearValue?: number;
}

function SummaryRow({ label, value, originalValue, prevYearValue }: SummaryRowProps) {
  return (
    <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50 rounded-lg">
      <span className="text-xs font-medium text-gray-700">{label}</span>
      <div className="flex items-center gap-3">
        <ReadOnlyNumberField value={value} originalValue={originalValue} bold small />
        {prevYearValue !== undefined && (
          <span className="text-xs font-medium text-gray-400 min-w-[70px] text-right tabular-nums">
            {formatNumber(prevYearValue)}
          </span>
        )}
      </div>
    </div>
  );
}
